import type { DemoSchedulingHours } from "@emr/contracts";
import { Icon } from "../Icon";

/**
 * The clinic's weekly opening hours on the booking page (PA-12): the clinic's own hours only,
 * never a physician's hours or closures, on the clinic's clock. Nothing renders when the service
 * sends none (an API older than PA-12).
 */

type Weekday = DemoSchedulingHours["weekday"];

const week: readonly (readonly [Weekday, string])[] = [
  ["monday", "Monday"],
  ["tuesday", "Tuesday"],
  ["wednesday", "Wednesday"],
  ["thursday", "Thursday"],
  ["friday", "Friday"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
];

/** "9:00 a.m." for a clinic wall-clock time (HH:mm), whatever the device's time zone. */
function wallClock(time: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  }).format(new Date(`1970-01-01T${time}:00Z`));
}

/** "9:00 a.m. – 5:00 p.m.", or null when the clinic is closed that day (or names no hours). */
function openHours(hours: readonly DemoSchedulingHours[], weekday: Weekday): string | null {
  const day = hours.find((entry) => entry.weekday === weekday);
  return day?.enabled ? `${wallClock(day.startTime)} – ${wallClock(day.endTime)}` : null;
}

/** The weekday it is now on the clinic's clock. */
export function clinicWeekday(now: Date, timeZone: string): Weekday {
  return new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone })
    .format(now)
    .toLowerCase() as Weekday;
}

/** The week, Monday first, with today marked. */
function ClinicWeek({
  hours,
  today,
}: {
  readonly hours: readonly DemoSchedulingHours[];
  readonly today: Weekday;
}) {
  return (
    <dl className="clinic-hours-week">
      {week.map(([weekday, label]) => (
        <div
          key={weekday}
          className={weekday === today ? "today" : undefined}
          aria-current={weekday === today ? "date" : undefined}
        >
          <dt>{label}</dt>
          <dd>{openHours(hours, weekday) ?? "Closed"}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Today's hours as a line a phone shows above the booking form, opening to the whole week.
 * `now` is for tests.
 */
export function ClinicHoursToday({
  hours,
  timeZone,
  now = new Date(),
}: {
  readonly hours: readonly DemoSchedulingHours[] | undefined;
  readonly timeZone: string;
  readonly now?: Date;
}) {
  if (hours === undefined || hours.length === 0) return null;
  const today = clinicWeekday(now, timeZone);
  const open = openHours(hours, today);
  return (
    <details className="clinic-hours">
      <summary>
        <Icon name="clock" />
        <span>{open === null ? "Closed today" : `Open today ${open}`}</span>
        <span className="clinic-hours-more">All hours</span>
      </summary>
      <ClinicWeek hours={hours} today={today} />
    </details>
  );
}

/** The whole week under its own heading, for the pages that replace the booking form. */
export function ClinicHoursWeek({
  hours,
  timeZone,
  now = new Date(),
}: {
  readonly hours: readonly DemoSchedulingHours[] | undefined;
  readonly timeZone: string;
  readonly now?: Date;
}) {
  if (hours === undefined || hours.length === 0) return null;
  return (
    <section className="clinic-hours-notice" aria-labelledby="clinic-hours-heading">
      <h2 id="clinic-hours-heading">
        <Icon name="clock" />
        Clinic hours
      </h2>
      <ClinicWeek hours={hours} today={clinicWeekday(now, timeZone)} />
    </section>
  );
}
